import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import {
  COHORT_PASSWORD_MIN_LENGTH,
  TEMP_PASSWORD_FOR_COHORTS,
} from '../../../../../constants/general';
import { deleteCohortAssignmentAndRefresh } from '../actions/general';

import type { AppDispatch } from '../../../../../../store';
import type { ChangeEvent } from 'react';
import type { IClientAssignment } from '../../../../../../utils/types';

type UseDeleteCohortAssignmentModalArgs = {
  open: boolean;
  assignment: IClientAssignment | null;
  clientId: string | undefined;
  onClose: () => void;
  onDeleted?: () => void;
};

export default function useDeleteCohortAssignmentModal({
  open,
  assignment,
  clientId,
  onClose,
  onDeleted,
}: UseDeleteCohortAssignmentModalArgs) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!open) {
      setPassword('');
    }
  }, [open]);

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleConfirmDelete = async () => {
    if (password !== TEMP_PASSWORD_FOR_COHORTS) {
      toast.error('Invalid password');
      return;
    }
    if (!assignment?.id) {
      toast.error('Cannot delete: assignment is missing an id.');
      onClose();
      return;
    }
    await deleteCohortAssignmentAndRefresh({
      dispatch,
      getAccessTokenSilently,
      assignmentId: assignment.id,
      clientId,
      onSuccess: () => {
        onDeleted?.();
        onClose();
      },
    });
  };

  return {
    password,
    handlePasswordChange,
    handleConfirmDelete,
    isSubmitDisabled: password.length < COHORT_PASSWORD_MIN_LENGTH,
  };
}
